export const IDENTIFIER_ANY = 'ANY';
export const MAX_IDENTIFIER_LENGTH = 256;
export const MIN_IDENTIFIER_SEARCH_LENGTH = 2;

/** Exact-match filters (device_uid, user_id, *_filter). Allows base64 and common ID chars. */
const IDENTIFIER_FILTER_PATTERN = /^[A-Za-z0-9+/=_\-.@|:]+$/;

/** ILIKE search — also blocks SQL wildcard metacharacters. */
const IDENTIFIER_SEARCH_PATTERN = /^[A-Za-z0-9+/=_\-.@|:]+$/;

export class IdentifierValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'IdentifierValidationError';
  }
}

const assertLength = (value: string, label: string): void => {
  if (value.length > MAX_IDENTIFIER_LENGTH) {
    throw new IdentifierValidationError(
      `${label} must be at most ${MAX_IDENTIFIER_LENGTH} characters.`
    );
  }
};

const assertFilterChars = (value: string, label: string): string => {
  assertLength(value, label);
  if (!IDENTIFIER_FILTER_PATTERN.test(value)) {
    throw new IdentifierValidationError(
      `${label} contains invalid characters. Use only letters, numbers, and + / = _ - . @ : |.`
    );
  }
  return value;
};

/** Validate a device/user filter value. Empty becomes ANY; ANY is allowed when enabled. */
export const normalizeIdentifierFilter = (
  value: string | undefined | null,
  label = 'Identifier',
  options?: { allowAny?: boolean }
): string => {
  const trimmed = (value ?? '').trim();
  if (!trimmed) {
    return options?.allowAny === false ? '' : IDENTIFIER_ANY;
  }
  if (options?.allowAny !== false && trimmed.toUpperCase() === IDENTIFIER_ANY) {
    return IDENTIFIER_ANY;
  }
  return assertFilterChars(trimmed, label);
};

/** Validate typeahead search input. Empty string is allowed (no query). */
export const normalizeIdentifierSearch = (
  value: string | undefined | null,
  label = 'Search term'
): string => {
  const trimmed = (value ?? '').trim();
  if (!trimmed) {
    return '';
  }
  if (trimmed.length < MIN_IDENTIFIER_SEARCH_LENGTH) {
    throw new IdentifierValidationError(
      `${label} must be at least ${MIN_IDENTIFIER_SEARCH_LENGTH} characters.`
    );
  }
  assertLength(trimmed, label);
  if (!IDENTIFIER_SEARCH_PATTERN.test(trimmed)) {
    throw new IdentifierValidationError(
      `${label} contains invalid characters. Use only letters, numbers, and + / = _ - . @ : |.`
    );
  }
  return trimmed;
};

export const isIdentifierValidationError = (error: unknown): error is IdentifierValidationError =>
  error instanceof IdentifierValidationError
  || (error instanceof Error && error.name === 'IdentifierValidationError');

/** Heuristic when the user pastes without picking device vs user explicitly. */
export const inferIdentifierType = (value: string): 'device_uid' | 'user_id' => {
  const trimmed = value.trim();
  const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(trimmed);
  const looksBase64 =
    /[+/=]/.test(trimmed)
    || (trimmed.length > 20 && /^[A-Za-z0-9+/=_-]+$/.test(trimmed));
  if (trimmed.includes('dev_') || trimmed.startsWith('device_') || (looksBase64 && !isUUID)) {
    return 'device_uid';
  }
  return 'user_id';
};
